import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  branch as fetchBranch, replyInBranch, readBranch, followBranch, sendFileDirect, sendFileGroup,
  react as sendReaction, setBranchAnswered, moveBranch, groupTopics
} from '../lib/chatLink';
import { toggleReaction } from '../lib/emoji';
import ChatTimeline from './ChatMessage';
import Composer from './Composer';
import EmojiPicker from './EmojiPicker';
import Modal from './Modal';
import {
  BranchIcon, MoreIcon, TickIcon, TopicIcon, answeredLine, applyChanges, extrasOf, inBranch, useMessageEdits
} from './ChatBranch';

/**
 * A BRANCH, OPEN: the message it is off, then its replies, then a box to
 * answer in. Gordon, 23 October: Slack's threads under the name Branches -
 * a side panel on a desktop (the topics pane folds while it is open, if the
 * screen is short of room), a sheet over the conversation on an upright
 * tablet, the whole screen on a phone. The layout is ChatPage.css's; this is
 * what is in it.
 *
 *   Follow / Following in the header: in a direct conversation both people
 *   follow every branch; in a group, its author, whoever replied or was
 *   @mentioned in it, and whoever pressed Follow.
 *
 *   "Also post to the conversation" under the box: for the answer everybody
 *   should see. Off each time - it is not remembered between replies.
 *
 *   Escape closes it and the page puts the focus back on the message.
 *
 * And, 27 October (096), two things done to the branch as a whole:
 *
 *   MARK ANSWERED - a tick in the header, for whoever started the branch,
 *   replied in it, owns the group or administers Jarvis (the server says,
 *   may_answer). A small line in the branch says who and when; "Answered" in
 *   the header, with Reopen. Quiet: nobody is told. A reply after it does not
 *   reopen it - a notice over the box says so, with Reopen beside it.
 *
 *   MOVE TO A TOPIC - in the header's ⋯ menu, groups only, for the starter,
 *   the owner or an administrator (may_move): a small dialog, the topic's
 *   name prefilled from the message's first line, New topic or one the group
 *   has. Afterwards the branch is empty and closed - "This branch was moved
 *   to the topic Boxes" - and the page opens the topic.
 *
 *   Neither is offered before migration 096 (answered_ready false).
 *
 * Everything a reply can carry, a message can: files, a voice note, a pasted
 * screenshot, emoji, reactions, Make a to-do, edit and delete. `conv` says
 * where files are sent ({ kind: 'direct', person } or { kind: 'group',
 * groupId }); `live` is the page's poll, handed down as { n, replies,
 * changes }, and a reply or a change in this branch reads it again.
 */

const coarse = () => {
  try { return window.matchMedia('(pointer: coarse)').matches; } catch { return false; }
};

const row = (m, scope) => ({
  key: m.seq || m.id,
  id: m.id,
  scope,
  reactions: Array.isArray(m.reactions) ? m.reactions : [],
  kind: m.kind === 'event' ? 'event' : 'message',
  body: m.body,
  at: m.created_at,
  author: m.author_name || 'Somebody',
  authorKey: m.author_key,
  automated: m.automated === true,
  mine: m.mine === true,
  files: Array.isArray(m.attachments) ? m.attachments : [],
  ...extrasOf(m)
});

/** The first line of the message, as a topic name of up to 60 characters - what the dialog prefills. */
export const titleOf = (body, max = 60) => {
  const line = String(body || '').split(/\r?\n/).map((s) => s.trim()).find(Boolean) || '';
  const flat = line.replace(/\s+/g, ' ');
  return flat.length > max ? `${flat.slice(0, max - 1).trimEnd()}…` : flat;
};

/**
 * "Marked answered by Rita Borg · 14:02" / "Reopened by …" as a line among
 * the replies, where it happened in time: a note row the timeline draws
 * small and in the middle, as it draws "added Marco". Keyed so the
 * timeline's New line and divider never land on it.
 */
const stateLine = (x, reopened) => ({
  key: `state-${reopened ? 'reopened' : 'answered'}-${x.at}`,
  id: null,
  scope: null,
  kind: 'event',
  body: answeredLine(x, reopened),
  at: x.at,
  author: '',
  authorKey: null,
  mine: false,
  files: [],
  reactions: [],
  state: true
});

/** The replies with the state lines slotted in by time (a line after the replies said before it). */
const withStateLines = (parentRow, replies, parent) => {
  const lines = [];
  if (parent?.answered) lines.push(stateLine(parent.answered, false));
  if (parent?.reopened) lines.push(stateLine(parent.reopened, true));
  if (!lines.length) return [parentRow, ...replies];
  const all = [...replies.map((m) => ({ m, t: new Date(m.at).getTime() })),
    ...lines.map((m) => ({ m, t: new Date(m.at).getTime(), line: true }))]
    .sort((a, b) => (a.t - b.t) || (a.line ? 1 : -1) - (b.line ? 1 : -1));
  return [parentRow, ...all.map((x) => x.m)];
};

/* ------------------------------------------------------ the move dialog */

/**
 * "Move to a topic…": the name, prefilled from the message's first line,
 * and New topic (the default) or one the group already has. Confirm moves
 * it. Errors the server gives (a name the group has, an archived topic) are
 * shown under the field, and the dialog stays open to try again.
 */
function MoveDialog({ scope, parentId, parent, groupId, onDone, onClose }) {
  const [mode, setMode] = useState('new');
  const [name, setName] = useState(() => titleOf(parent?.body));
  const [topics, setTopics] = useState(null);
  const [chosen, setChosen] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const field = useRef(null);
  useEffect(() => {
    let live = true;
    groupTopics(groupId)
      .then((rows) => {
        if (!live) return;
        // The topics the branch could go to: not General (it is already in
        // the group), not the one it is in, not an archived one.
        const list = (Array.isArray(rows) ? rows : []).filter((t) => t.id && !t.archived_at
          && String(t.id) !== String(parent?.topicId || ''));
        setTopics(list);
        if (list[0]) setChosen(String(list[0].id));
      })
      .catch(() => { if (live) setTopics([]); });
    return () => { live = false; };
  }, [groupId, parent]);
  // After the Modal has put the focus on its panel: into the name.
  useEffect(() => {
    if (mode !== 'new') return undefined;
    const t = setTimeout(() => field.current?.focus({ preventScroll: true }), 40);
    return () => clearTimeout(t);
  }, [mode]);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const done = await moveBranch(scope, parentId, mode === 'new' ? { topic: name.trim() } : { topicId: chosen });
      onDone(done);
    } catch (err) {
      setError(err.response?.data?.error || 'That branch did not move');
      setBusy(false);
    }
  };
  const can = mode === 'new' ? Boolean(name.trim()) : Boolean(chosen);
  return (
    <Modal
      title="Move to a topic"
      subtitle="The replies become the topic's conversation, in order, and the message stays here with a link to it."
      onClose={onClose}
      footer={(
        <div className="ch-dialog-actions">
          <button type="button" className="ch-flat" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="submit" form="ch-move-branch" className="ch-primary" disabled={busy || !can}>
            {busy ? 'Moving…' : 'Move'}
          </button>
        </div>
      )}
    >
      <form id="ch-move-branch" className="ch-form ch-move-form" onSubmit={submit}>
        <div className="ch-move-choice" role="radiogroup" aria-label="Where to">
          <label className="ch-move-option">
            <input type="radio" name="ch-move-mode" value="new" checked={mode === 'new'} onChange={() => setMode('new')} />
            <span>New topic</span>
          </label>
          <label className="ch-move-option" data-off={!(topics || []).length || undefined}>
            <input type="radio" name="ch-move-mode" value="existing" checked={mode === 'existing'}
              disabled={!(topics || []).length} onChange={() => setMode('existing')} />
            <span>{topics && !topics.length ? 'An existing topic (the group has none yet)' : 'An existing topic'}</span>
          </label>
        </div>
        {mode === 'new' ? (
          <>
            <label className="ch-field-label" htmlFor="ch-move-name">Topic name</label>
            <input
              ref={field}
              id="ch-move-name"
              className="ch-field"
              type="text"
              maxLength={80}
              value={name}
              placeholder="What the topic is about"
              onChange={(e) => setName(e.target.value)}
            />
          </>
        ) : (
          <>
            <label className="ch-field-label" htmlFor="ch-move-topic">Topic</label>
            <select id="ch-move-topic" className="ch-field" value={chosen} onChange={(e) => setChosen(e.target.value)}>
              {(topics || []).map((t) => <option key={t.id} value={t.id}>{t.topic}</option>)}
            </select>
          </>
        )}
        <p className="ch-move-note">
          Everybody following this branch is told once, and the replies keep their files, reactions and mentions.
        </p>
        {error && <p className="ch-attach-error" role="alert">{error}</p>}
      </form>
    </Modal>
  );
}

/* --------------------------------------------------------- the ⋯ menu */

/** The header's ⋯: what can be done to the branch as a whole. */
function PanelMenu({ items, onClose }) {
  const panel = useRef(null);
  useEffect(() => {
    panel.current?.querySelector('button')?.focus({ preventScroll: true });
    const away = (e) => { if (panel.current && !panel.current.contains(e.target)) onClose(); };
    const esc = (e) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose(); } };
    document.addEventListener('mousedown', away, true);
    document.addEventListener('keydown', esc, true);
    return () => {
      document.removeEventListener('mousedown', away, true);
      document.removeEventListener('keydown', esc, true);
    };
  }, [onClose]);
  return (
    <div ref={panel} className="ch-msg-menu ch-branch-menu" role="menu" aria-label="Branch">
      {items.map((it) => (
        <button key={it.label} type="button" role="menuitem" onClick={() => { onClose(); it.run(); }}>
          <span aria-hidden="true" className="ch-sheet-glyph">{it.glyph}</span> {it.label}
        </button>
      ))}
    </div>
  );
}

export default function BranchPanel({
  scope, parentId, focusId = null, conv, convName, alsoLabel, me, meKey, onClose, onMakeTodo = null,
  live = null, onChanged = null, onOpenTopic = null
}) {
  const [data, setData] = useState(null);
  const [messages, setMessages] = useState([]);
  const [error, setError] = useState(null);
  const [following, setFollowing] = useState(false);
  const [also, setAlso] = useState(false);
  const [unreadKey, setUnreadKey] = useState(undefined);
  const [picker, setPicker] = useState(null);
  const [trouble, setTrouble] = useState(null);
  const [menu, setMenu] = useState(false);
  const [moving, setMoving] = useState(false);
  const [marking, setMarking] = useState(false);
  const composer = useRef(null);
  const messagesRef = useRef([]);
  useEffect(() => { messagesRef.current = messages; }, [messages]);
  const place = `branch:${scope}:${parentId}`;

  const load = useCallback(async (first = false) => {
    try {
      const got = await fetchBranch(scope, parentId);
      const parentRow = row(got.parent, scope);
      const rows = withStateLines(parentRow, (got.replies || []).map((m) => row(m, scope)), parentRow);
      setData(got);
      setFollowing(got.following === true);
      setMessages(rows);
      setError(null);
      if (first) {
        // The "New" line at the first reply after where I had read to - for
        // somebody following; a branch I am only looking at has nothing new.
        const since = got.read_to ? new Date(got.read_to).getTime() : -Infinity;
        const firstNew = got.following
          ? (got.replies || []).find((m) => !m.mine && new Date(m.created_at).getTime() > since) : null;
        setUnreadKey(firstNew ? (firstNew.seq || firstNew.id) : null);
      }
      readBranch(scope, parentId).then(() => onChanged && onChanged()).catch(() => {});
    } catch (err) {
      setError(err.response?.status === 404 ? 'That message is not in a conversation you can open any more.'
        : (err.response?.data?.error || 'Could not open the branch'));
      setUnreadKey(null);
    }
  }, [scope, parentId, onChanged]);

  useEffect(() => {
    setData(null);
    setMessages([]);
    setUnreadKey(undefined);
    setAlso(false);
    setMenu(false);
    setMoving(false);
    load(true);
  }, [load]);

  // Into the box on opening, on a desktop - a phone's keyboard would cover it.
  useEffect(() => {
    if (!data || coarse()) return;
    composer.current?.focus();
  }, [data]);

  // The poll: a reply in this branch, or a change to one of its messages.
  // What the poll had said before this branch opened is not news to it.
  const seen = useRef(live ? live.n : null);
  useEffect(() => {
    if (!live || seen.current === live.n) return;
    seen.current = live.n;
    const mine = (live.replies || []).some((r) => r.scope === scope && String(r.parent_id) === String(parentId));
    const ids = new Set(messagesRef.current.filter((m) => m.id).map((m) => String(m.id)));
    const changed = (live.changes || []).filter((c) => c.scope === scope && ids.has(String(c.id)));
    if (changed.length) setMessages((prev) => applyChanges(prev, changed));
    if (mine) load(false);
    if ((live.reactions || []).some((r) => ids.has(String(r.message_id)))) load(false);
  }, [live, scope, parentId, load]);

  // Escape closes the panel - unless something inside it (a list, a picker,
  // a message being edited, the move dialog) has already taken it.
  useEffect(() => {
    const esc = (e) => {
      if (e.key !== 'Escape' || e.defaultPrevented || moving) return;
      onClose();
    };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [onClose, moving]);

  const say = setTrouble;
  const edits = useMessageEdits(setMessages, say, useMemo(() => [
    ...(data?.may_mention_everyone ? [{ key: 'everyone', name: 'everyone' }] : []), ...(data?.mention_people || [])
  ], [data]));

  const toggleFollow = async () => {
    const next = !following;
    setFollowing(next);
    try {
      await followBranch(scope, parentId, next);
      if (onChanged) onChanged();
    } catch (err) {
      setFollowing(!next);
      say(err.response?.data?.error || 'That did not change');
    }
  };

  /** Mark answered, or reopen: the state line and the header follow the server's answer. */
  const setAnswered = async (answered) => {
    if (marking) return;
    setMarking(true);
    try {
      const got = await setBranchAnswered(scope, parentId, answered);
      setData((prev) => (prev ? { ...prev, parent: { ...prev.parent, answered: got.answered, reopened: got.reopened } } : prev));
      setMessages((prev) => {
        const [p, ...rest] = prev;
        if (!p) return prev;
        const parentRow = { ...p, answered: got.answered, reopened: got.reopened };
        return withStateLines(parentRow, rest.filter((m) => !m.state), parentRow);
      });
      setTrouble(null);
      if (onChanged) onChanged();
    } catch (err) {
      say(err.response?.data?.error || (answered ? 'That branch was not marked answered' : 'That branch was not reopened'));
    } finally {
      setMarking(false);
    }
  };

  const send = async ({ body, file, mentions }) => {
    try {
      let saved;
      if (file) {
        const opts = { mentions, branch: parentId, also };
        saved = conv.kind === 'group'
          ? await sendFileGroup(conv.groupId, file, body, null, opts)
          : await sendFileDirect(conv.person, file, body, null, opts);
      } else {
        saved = await replyInBranch(scope, parentId, body, { also, mentions });
      }
      setMessages((prev) => [...prev, row({ ...saved, mine: true, author_name: saved?.author_name || 'You' }, scope)]);
      setAlso(false);
      setTrouble(null);
      if (onChanged) onChanged();
    } catch (err) {
      const why = err.response?.data?.error || (err.response?.status === 413 ? 'That file is too large to send.' : null);
      if (file) throw Object.assign(new Error('not sent'), { fileTrouble: why || `${file.name} did not send.` });
      say(why || 'That reply did not send');
      throw err;
    }
  };

  const reactSeq = useRef(new Map());
  const toggleReact = (m, emoji) => {
    const was = messagesRef.current.find((x) => x.id === m.id)?.reactions || [];
    const n = (reactSeq.current.get(m.id) || 0) + 1;
    reactSeq.current.set(m.id, n);
    const set = (reactions) => setMessages((prev) => prev.map((x) => (x.id === m.id ? { ...x, reactions } : x)));
    set(toggleReaction(was, emoji));
    sendReaction({ scope, id: m.id, emoji })
      .then((got) => { if (reactSeq.current.get(m.id) === n && Array.isArray(got?.reactions)) set(got.reactions); })
      .catch((err) => { if (reactSeq.current.get(m.id) === n) { set(was); say(err.response?.data?.error || 'That reaction did not save'); } });
  };

  const parent = messages[0] || null;
  const replies = messages.slice(1).filter((m) => !m.state);
  const count = replies.length;
  const parentAuthor = parent ? (parent.mine ? 'your' : `${parent.author.split(/\s+/)[0]}’s`) : 'this';
  const where = data?.conversation;
  const sub = where?.kind === 'group'
    ? `#${where.name}${where.topic ? ` · ${where.topic}` : ''}`
    : (where?.with ? `${where.with.is_me ? 'Just you' : where.with.name}${where.topic ? ` · ${where.topic}` : ''}` : convName);
  const divider = useMemo(() => (parent ? { after: parent.key, label: count ? inBranch(count) : 'No replies yet' } : null),
    [parent, count]);

  // 096: what the server says may be done here, and the state the branch is in.
  const answered = parent?.answered || null;
  const moved = parent?.moved || null;
  const mayAnswer = data?.answered_ready === true && data?.may_answer === true && !moved;
  const mayMove = data?.answered_ready === true && data?.may_move === true && !moved && scope === 'group' && conv?.kind === 'group';
  // A reply after the mark: the branch did not reopen itself - say so.
  const repliedSince = Boolean(answered) && replies.some((m) => new Date(m.at).getTime() > new Date(answered.at).getTime());
  const menuItems = useMemo(() => (mayMove
    ? [{ label: 'Move to a topic…', glyph: <TopicIcon size={14} />, run: () => setMoving(true) }] : []), [mayMove]);
  const openTopic = () => {
    if (!moved) return;
    if (onOpenTopic) onOpenTopic(moved);
  };

  return (
    <section className="ch-branch" aria-label="Branch" data-branch-panel="true" data-answered={answered ? 'true' : undefined}
      data-moved={moved ? 'true' : undefined}>
      <header className="ch-branch-head">
        <button type="button" className="ch-back ch-branch-back" onClick={onClose}>‹ Back</button>
        <span className="ch-branch-mark" aria-hidden="true"><BranchIcon size={16} /></span>
        <div className="ch-branch-titles">
          <h2 className="ch-branch-title">
            Branch
            {answered && <span className="ch-branch-state"><TickIcon size={12} /> Answered</span>}
            {moved && <span className="ch-branch-state ch-branch-state-moved"><TopicIcon size={12} /> Moved</span>}
          </h2>
          {sub && <p className="ch-branch-sub">{sub}</p>}
        </div>
        {mayAnswer && !answered && (
          <button type="button" className="ch-flat ch-small ch-answer" disabled={marking} onClick={() => setAnswered(true)}
            aria-label="Mark answered" title="Mark this branch answered - the question is settled">
            <TickIcon size={14} /><span className="ch-answer-word">Mark answered</span>
          </button>
        )}
        {mayAnswer && answered && (
          <button type="button" className="ch-flat ch-small ch-answer ch-reopen" disabled={marking} onClick={() => setAnswered(false)}
            title={answeredLine(answered)}>
            Reopen
          </button>
        )}
        <button type="button" className="ch-flat ch-small ch-follow" aria-pressed={following}
          disabled={!data} onClick={toggleFollow}
          title={following ? 'You are told about new replies. Press to stop.' : 'Be told about new replies'}>
          {following ? 'Following' : 'Follow'}
        </button>
        {menuItems.length > 0 && (
          <span className="ch-branch-more">
            <button type="button" className="ch-flat ch-small ch-branch-more-tap" aria-label="More actions"
              aria-haspopup="menu" aria-expanded={menu} title="More" onClick={() => setMenu((v) => !v)}>
              <MoreIcon size={15} />
            </button>
            {menu && <PanelMenu items={menuItems} onClose={() => setMenu(false)} />}
          </span>
        )}
        <button type="button" className="ch-flat ch-small ch-branch-close" aria-label="Close the branch"
          title="Close (Esc)" onClick={onClose}>✕</button>
      </header>
      {(trouble || error) && <div className="ch-warn" role="status">{trouble || error}</div>}
      {data && (
        <ChatTimeline
          messages={messages}
          place={place}
          ready={Boolean(data)}
          unreadKey={unreadKey}
          divider={divider}
          inBranch
          label="Branch"
          onFileError={say}
          onReact={toggleReact}
          onMoreReactions={(m, anchor) => setPicker({ m, anchor })}
          onMakeTodo={onMakeTodo}
          onBranch={() => composer.current?.focus()}
          onEdit={edits.onEdit}
          onDelete={edits.onDelete}
          onEditSave={edits.onEditSave}
          onEditCancel={edits.onEditCancel}
          editing={edits.editing}
          me={me}
          meKey={meKey}
          focusId={focusId}
          focusNonce={focusId}
          empty={null}
        />
      )}
      {data && count === 0 && !moved && (
        <p className="ch-empty ch-branch-empty">
          Answer {parentAuthor} message here. Only the people following this branch are told -
          tick “Also post” if everybody should see it. A new subject? Start a topic instead.
        </p>
      )}
      {!data && !error && <p className="ch-empty">Opening the branch…</p>}
      {data && moved && (
        <div className="ch-branch-notice ch-branch-notice-moved" role="status">
          <TopicIcon size={14} />
          <span>
            This branch was moved to the topic <strong>{moved.topic || 'a topic'}</strong>
            {moved.by_name ? ` by ${moved.by_name}` : ''} - the replies are there now.
          </span>
          {onOpenTopic && (
            <button type="button" className="ch-flat ch-small" onClick={openTopic}>Open the topic</button>
          )}
        </div>
      )}
      {data && !moved && answered && repliedSince && (
        <div className="ch-branch-notice" role="status">
          <TickIcon size={14} />
          <span>This branch was marked answered</span>
          {mayAnswer && (
            <button type="button" className="ch-flat ch-small" disabled={marking} onClick={() => setAnswered(false)}>Reopen</button>
          )}
        </div>
      )}
      {data && !moved && data.may_reply !== false && (
        <Composer
          ref={composer}
          id="ch-branch-draft"
          place={place}
          label="Reply"
          placeholder="Reply in this branch…"
          onSend={send}
          mentionsOn
          people={data.mention_people || []}
          everyone={data.may_mention_everyone === true}
          above={(
            <label className="ch-also">
              <input type="checkbox" checked={also} onChange={(e) => setAlso(e.target.checked)} />
              <span>{alsoLabel || 'Also post to the conversation'}</span>
            </label>
          )}
        />
      )}
      {picker && (
        <EmojiPicker
          key={`branch-react-${picker.m?.id}`}
          mode="react"
          anchor={picker.anchor}
          label="Add a reaction"
          onPick={(char, { keepOpen }) => { toggleReact(picker.m, char); if (!keepOpen) setPicker(null); }}
          onClose={() => setPicker(null)}
        />
      )}
      {moving && data && (
        <MoveDialog
          scope={scope}
          parentId={parentId}
          parent={{ body: parent?.body, topicId: where?.topic_id || null }}
          groupId={conv.groupId}
          onClose={() => setMoving(false)}
          onDone={(done) => {
            setMoving(false);
            if (onChanged) onChanged();
            if (onOpenTopic && done?.topic) onOpenTopic({ topic_id: done.topic.id, topic: done.topic.topic });
            else load(false);
          }}
        />
      )}
    </section>
  );
}
